import { module } from 'modujs'
import { VAsset, VInputSelect } from '../vue/components'
import { computed, createApp, nextTick, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue/dist/vue.esm-bundler.js'
import { gsap } from '../lib/gsap'
import Swiper from 'swiper'
import { Navigation } from 'swiper/modules'
import { CUSTOM_EVENT, ENV, MODAL_CONTENT_KEYS, VARS, VUE_SETTINGS } from '../config'
import { DEFAULT_DURATION } from '../lib/gsap'

export default class Schedule extends module {

    static get settings() {
        return {
            CLASS_READY: 'is-ready',
        }
    }

    constructor(m) {
        super(m)

        // DOM
        this.$el = this.el

        // Vars
        this.DEFAULT_ENDPOINT = this.getData('default-url')
        this.data = {}
    }

    init() {
        this.fetch().then(() => this.initVue())
    }

    fetch(url) {
        url = url || this.DEFAULT_ENDPOINT

        return new Promise((resolve, reject) => {
            fetch(url)
                .then(r => r.json())
                .then(({ data, meta}) => {
                    // Parse data
                    const schedule = data.map(item => {
                        const dayName = item.timeframe.start.dayOfTheWeek
                        const dayNumber = item.timeframe.start.day

                        return {
                            dayName,
                            dayNumber,
                            ...item
                        }
                    })

                    this.data = {
                        schedule,
                        meta,
                    }
                })
                .then(() => {
                    resolve(this.data)
                })
                .catch(e => reject(e))
        })
    }

    initVue() {

        this.vueApp = createApp({
            ...VUE_SETTINGS,
            components: {
                VAsset,
                VInputSelect,
            },
            setup: () => {

                let swiper

                // DOM References
                const swiperRef = ref(null)

                // Reactive Data
                const schedule = ref(this.data.schedule)
                const meta = ref(this.data.meta)
                const currentId = ref(-1)
                const currentIndex = ref(-1)
                const currentModalId = ref(null)
                const timezone = reactive({
                    $model: this.DEFAULT_ENDPOINT
                })

                // Computed
                const currentScheduleItems = computed(() => schedule.value.find(item => item.id === currentId.value)?.items)

                // Methods
                const setCurrent = (index, updateSwiper = true, scroll = true) => {
                    currentIndex.value = index
                    currentId.value = schedule.value[index]?.id
                    if(scroll) {
                        this.call('scrollTo', { target: this.$el }, 'Scroll')
                    }
                    if(updateSwiper) {
                        swiper?.slideTo(index)
                    }
                }
                const onEnter = (el, done) => {
                    const $items = Array.from(el.children)
                    if(ENV.REDUCED_MOTION) {
                        gsap.from(el, {
                            opacity: 0,
                            duration: 0.5,
                            onComplete: done
                        })
                    } else {
                        gsap.from($items, {
                            y: 100,
                            opacity: 0,
                            duration: 0.5,
                            ease: 'sine.out',
                            stagger: 0.1,
                            onComplete: done
                        })
                    }
                }
                const onLeave = (el, done) => {
                    gsap.to(el, {
                        // y: 100,
                        opacity: 0,
                        duration: 0.2,
                        ease: 'sine.in',
                        onComplete: done
                    })
                }
                const openModal = id => {
                    currentModalId.value = id
                    this.call('open', { type: MODAL_CONTENT_KEYS.SHOW, id }, 'ModalContent')
                }
                const closeModal = () => currentModalId.value = null
                const previous = () => {
                    console.log('previous')
                    if(VARS.window.width < 500) {
                        setCurrent(currentIndex.value - 1, false)
                    } else {
                        swiper?.slidePrev()
                    }
                }
                const next = () => {
                    if(VARS.window.width < 500) {
                        setCurrent(currentIndex.value + 1, false)
                    } else {
                        console.log('next')
                        swiper?.slideNext()
                    }
                }

                // Watchers
                watch(timezone, newTimezone => {
                    const url = newTimezone.$model
                    this.$el.classList.remove(Schedule.settings.CLASS_READY)
                    this.fetch(url).then(data => {
                        this.vueMountedApp.schedule.length = 0
                        this.vueMountedApp.schedule.push(...data.schedule)
                        this.vueMountedApp.meta = data.meta

                        const index = Math.max(0, this.vueMountedApp.schedule.findIndex(item => item.id === currentId.value))
                        setCurrent(index)

                        nextTick(() => {
                            this.$el.classList.add(Schedule.settings.CLASS_READY)
                            swiper?.refresh()
                            // swiper?.go(currentIndex.value)
                        })
                    })
                })
                const initSwiper = () => {

                    swiper?.destroy()

                    const options = {
                        // modules: [ Navigation ],
                        // navigation: {
                        //     prevEl: $prev,
                        //     nextEl: $next,
                        // },
                        pagination: false,
                        slidesPerView: 'auto',
                        slidesPerGroupAuto: 'auto',
                        spaceBetween: 0,
                        speed: 1000 * DEFAULT_DURATION,
                    }
                    console.log(swiperRef)

                    swiper = new Swiper(swiperRef.value, options)

                    swiper?.slideTo(currentIndex.value)
                }

                // Lifecycle
                onBeforeMount(() => {
                    const activeItemIndex = schedule.value.findIndex(item => item.active)
                    setCurrent(activeItemIndex > -1 ? activeItemIndex : 0, true, false)
                })
                onMounted(() => {
                    this.$el.classList.add(Schedule.settings.CLASS_READY)
                    window.addEventListener(CUSTOM_EVENT.RESIZE_END, initSwiper)
                    initSwiper()
                })
                onBeforeUnmount(() => {
                    swiper?.destroy()
                    window.removeEventListener(CUSTOM_EVENT.RESIZE_END, initSwiper)
                })

                return {
                    swiperRef,
                    schedule,
                    meta,
                    currentId,
                    currentIndex,
                    currentModalId,
                    timezone,
                    currentScheduleItems,
                    setCurrent,
                    onEnter,
                    onLeave,
                    openModal,
                    closeModal,
                    previous,
                    next,
                }
            }
        })
        this.vueMountedApp = this.vueApp.mount(this.$el)
    }

    onModalClose() {
        this.vueMountedApp.closeModal()
    }

    destroy() {
        this.vueApp?.unmount()
    }
}
